/**
 * Programme descriptor data entry point.
 *
 * The single public entry for transforming a {@link Programme} into the flat
 * {@link DescriptorData} expected by the tagged QQI Programme Descriptor template
 * (docx-templates).
 *
 * The version-agnostic field mapping and shared services live in the shared core
 * (`./descriptor-core`); the variant-specific mapping lives in the per-variant
 * mappers (the non-CAS mapper, `./non-cas-descriptor-data`, and the CAS mapper,
 * `./cas-descriptor-data`). This entry resolves the descriptor variant
 * ({@link resolveDescriptorVariant}) and routes a CAS programme through the CAS
 * mapper and a non-CAS programme through the non-CAS mapper. The descriptor
 * output types and shared helpers are re-exported here so existing import sites
 * stay stable.
 *
 * @module export/descriptor-data
 */

import type { Programme } from "@/types";

import { buildCasDescriptorData } from "./cas-descriptor-data";
import type { DescriptorBuildContext, DescriptorData } from "./descriptor-core";
import { resolveDescriptorVariant } from "./descriptor-variant";
import { buildNonCasDescriptorData } from "./non-cas-descriptor-data";

export * from "./descriptor-core";

/**
 * Transform a Programme into the data object expected by the tagged template.
 *
 * The programme is classified by {@link resolveDescriptorVariant}: CAS
 * programmes are mapped by the CAS mapper, all others by the non-CAS mapper.
 *
 * @param p - The full programme state
 * @returns DescriptorData ready for docx-templates createReport()
 */
export function buildDescriptorData(
  p: Programme,
  context: DescriptorBuildContext = {},
): DescriptorData {
  if (resolveDescriptorVariant(p) === "cas") {
    return buildCasDescriptorData(p, context);
  }
  return buildNonCasDescriptorData(p, context);
}
